import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { extractApiError, type ApiErrorInfo } from '@/api/errorStatus';
import { rejectedApiError } from './rejectedApiError';
import { authApi } from '@/api';
import { clearBalanceSync } from '@/utils/billingSync';
import { tokenStorage, refreshTokenStorage, projectStorage } from '@/api/client';
import { pickStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import type {
  AuthState,
  LoginResult,
  TwoFactorMethod,
  User,
  UpdateProfilePayload,
} from '@/types';

// WEB-133 §F10 — shape of the 202 challenge body returned by /auth/login and
// /auth/google when the account has 2FA enabled. `challenge` is the always-'2fa'
// discriminator; the client must complete POST /auth/2fa/verify before tokens
// are issued. The challengeToken is NOT an access token and is never stored.
interface ChallengeBody {
  challenge?: string;
  method?: TwoFactorMethod;
  challengeToken?: string;
}

function isChallenge(data: unknown): data is Required<ChallengeBody> {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as ChallengeBody).challenge === '2fa'
  );
}

const initialState: AuthState = {
  user: null,
  isLoading: true,
  error: null,
  isAuthenticated: false,
};

// WEB-1299: shown on the login page after a logout whose server call failed
// (network blip, deploy restart). clearClientState() in logoutThunk below
// always runs first, so the user IS logged out locally either way — this
// just tells them the server round-trip itself did not succeed.
// WEB-1874: текст переехал в общий словарь; язык берётся текущий (thunk
// выполняется вне React).
const logoutServerUnreachableMessage = (): string =>
  pickStrings(commonStrings).logoutServerUnreachable;

/**
 * WEB-393: start a fresh authenticated session — persist the new token AND drop
 * the previous user's per-user localStorage (activeProjectId). Without the clear,
 * logging in as another user without an explicit logout leaks the prior user's
 * project into the switcher (403-flashes; backend still rejects, so no data leak).
 *
 * WEB-1114: also persists the refresh token — every login/register/2fa/google
 * response has always included one, the client just never stored it, so the
 * axios response interceptor (client.ts) had nothing to silently renew with.
 */
const startCleanSession = (accessToken: string, refreshToken: string): void => {
  tokenStorage.set(accessToken);
  refreshTokenStorage.set(refreshToken);
  projectStorage.remove();
  // WEB-936 п.5 (приёмка): сигнальный ключ баланса — тоже данные пользователя.
  // Правило этого места (WEB-393) — не наследовать НИЧЕГО от предыдущего.
  clearBalanceSync();
};

export const loginThunk = createAsyncThunk<
  LoginResult,
  { email: string; password: string },
  { rejectValue: ApiErrorInfo }
>(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const loginResponse = await authApi.login(email, password);
      // WEB-133 §F10: a 2FA-enabled account returns 202 + challenge instead of
      // tokens. Surface the challenge WITHOUT storing a token or calling /me —
      // the challengeToken must never be treated as an access token.
      if (isChallenge(loginResponse.data)) {
        return {
          status: '2fa_required',
          method: loginResponse.data.method,
          challengeToken: loginResponse.data.challengeToken,
        };
      }
      startCleanSession(loginResponse.data.accessToken, loginResponse.data.refreshToken);
      const meResponse = await authApi.me();
      return { status: 'authenticated', user: meResponse.data as User };
    } catch (error: unknown) {
      // WEB-1955: отказ отвергается РАЗОБРАННЫМ (статус, код, причина).
      // Прежняя пара «текст, а если не Error — английская константа» теряла
      // код ещё в слайсе: экран входа не мог спросить реестр и показывал
      // русскому человеку английскую строку, англоязычному — русскую фразу
      // сервера. Запасную фразу даёт экран вторым аргументом localizeError —
      // она у него на обоих языках.
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const verify2faThunk = createAsyncThunk<
  User,
  { challengeToken: string; code?: string; recoveryCode?: string },
  { rejectValue: ApiErrorInfo }
>(
  'auth/verify2fa',
  async (payload, { rejectWithValue }) => {
    try {
      // Completes the login challenge. Only on success do we receive — and
      // store — a real access token, mirroring the normal login success path.
      const verifyResponse = await authApi.verify2fa(payload);
      startCleanSession(verifyResponse.data.accessToken, verifyResponse.data.refreshToken);
      const meResponse = await authApi.me();
      return meResponse.data as User;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const registerThunk = createAsyncThunk(
  'auth/register',
  async (
    {
      email,
      password,
      username,
      termsAccepted,
    }: { email: string; password: string; username?: string; termsAccepted: boolean },
    { rejectWithValue }
  ) => {
    try {
      const registerResponse = await authApi.register(
        email,
        password,
        username,
        termsAccepted
      );
      startCleanSession(registerResponse.data.accessToken, registerResponse.data.refreshToken);
      const meResponse = await authApi.me();
      return meResponse.data as User;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const googleAuthThunk = createAsyncThunk<
  LoginResult,
  string,
  { rejectValue: ApiErrorInfo }
>(
  'auth/google',
  async (accessToken: string, { rejectWithValue }) => {
    try {
      const googleResponse = await authApi.googleAuth(accessToken);
      // WEB-133 §F10: same 202 challenge contract as password login.
      if (isChallenge(googleResponse.data)) {
        return {
          status: '2fa_required',
          method: googleResponse.data.method,
          challengeToken: googleResponse.data.challengeToken,
        };
      }
      // Persist the access token before any authenticated request, otherwise
      // the follow-up /auth/me call goes out without an Authorization header
      // and returns 401 — same pattern as login/register thunks above.
      startCleanSession(googleResponse.data.accessToken, googleResponse.data.refreshToken);
      const meResponse = await authApi.me();
      return { status: 'authenticated', user: meResponse.data as User };
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

/**
 * WEB-1848 — why a logout needs to know its own cause.
 *
 * When a silent refresh fails, client.ts's onSessionExpired ALREADY shows
 * "Сессия истекла — войдите снова" and only THEN dispatches this thunk — with
 * the access token already wiped. So POST /auth/logout goes out without an
 * Authorization header, the server answers 401, and .rejected added a second,
 * contradicting line on the login screen: "Выход выполнен локально, связь с
 * сервером не удалась" — which is not even true (the server was reachable; we
 * had removed our own pass). One cause must have exactly one message.
 */
export interface LogoutOptions {
  /** true only for the forced logout that follows an expired session. */
  readonly sessionExpired?: boolean;
}

export const logoutThunk = createAsyncThunk<
  void,
  LogoutOptions | undefined,
  { rejectValue: ApiErrorInfo }
>(
  'auth/logout',
  async (_options, { rejectWithValue }) => {
    // Clear all per-user localStorage regardless of whether the server call
    // succeeds — otherwise stale token/projectId leaks into the next session
    // and causes 403 "not a member of project" on first request.
    const clearClientState = () => {
      tokenStorage.remove();
      refreshTokenStorage.remove();
      projectStorage.remove();
      // WEB-936 п.5 (приёмка): на общем компьютере от предыдущего человека не
      // должно оставаться следа его денег, пусть даже это одно число.
      clearBalanceSync();
    };

    try {
      await authApi.logout();
      clearClientState();
    } catch (error: unknown) {
      clearClientState();
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const fetchMeThunk = createAsyncThunk(
  'auth/me',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.me();
      return response.data as User;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const updateProfileThunk = createAsyncThunk(
  'auth/updateProfile',
  async (payload: UpdateProfilePayload, { rejectWithValue }) => {
    try {
      const response = await authApi.updateProfile(payload);
      return response.data as User;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const requestPasswordResetThunk = createAsyncThunk(
  'auth/requestPasswordReset',
  async (email: string, { rejectWithValue }) => {
    try {
      await authApi.requestPasswordReset(email);
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

// WEB-229 — step 2 of password-reset: POST /auth/verify-reset-password-code
// Exchanges the 6-digit CODE from email for a one-time reset TOKEN.
// Returns the token string so the component can store it for step 3.
export const verifyResetPasswordCodeThunk = createAsyncThunk<
  string,
  { email: string; code: string },
  { rejectValue: ApiErrorInfo }
>(
  'auth/verifyResetPasswordCode',
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const response = await authApi.verifyResetPasswordCode(email, code);
      return response.data.token as string;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const resetPasswordThunk = createAsyncThunk(
  'auth/resetPassword',
  async (
    { token, newPassword }: { token: string; newPassword: string },
    { rejectWithValue }
  ) => {
    try {
      await authApi.resetPassword(token, newPassword);
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const sendVerificationCodeThunk = createAsyncThunk(
  'auth/sendVerificationCode',
  async (email: string, { rejectWithValue }) => {
    try {
      await authApi.sendVerificationCode(email);
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

/**
 * WEB-1592 (проявление 3) — результат подтверждения почты.
 *
 * `sessionIssued` — сервер выдал рабочую пару токенов вместе с подтверждением,
 * и она РЕАЛЬНО записана в хранилище этим самым вызовом. Это единственный
 * честный признак «человек уже в сессии»: экран подтверждения не имеет права
 * решать по флагу `isAuthenticated` из своего замыкания — на «чужом»
 * устройстве тот равен false на момент создания обработчика и таким и
 * останется, что бы дальше ни положил редьюсер. Именно из-за этого кабинет
 * выбрасывал уже выданную сервером сессию и уводил человека на форму входа.
 * До 24.08 это ещё и запирало снаружи (сервер стирал пароль и второй фактор);
 * решением владельца WEB-1612 стирание снято, остались отзыв прежних сессий и
 * письмо, но выбрасывать выданную сессию кабинет по-прежнему не имеет права.
 */
/**
 * WEB-1599 (подтема 6) — признак «почта подтверждена, но сессию обновить не
 * вышло».
 *
 * Ровно та строка, что уходит в rejectWithValue из ветки «сервер подтвердил
 * почту, но пары токенов не выдал». Экспортирована, чтобы экран
 * подтверждения (VerifyEmail.tsx) отличал её от обычного отказа и НЕ говорил
 * человеку «Не удалось подтвердить»: почта уже подтверждена, код сгорел,
 * повторная отправка бессмысленна — выход один, на форму входа.
 *
 * Почему отказ (rejected), а не успех с флагом: сторожа WEB-1217 (ревизия,
 * коммит 8cc0e050) и WEB-1592 держат именно rejected для этой ветки — без
 * свежей пары токенов кабинет не имеет права ни патчить пользователя в
 * сторе, ни поднимать isAuthenticated (следующий запрос уйдёт со старым
 * токеном и получит 403 EMAIL_NOT_VERIFIED без возможности восстановиться).
 * Меняется только ТЕКСТ и выход для человека, не механика.
 */
export const EMAIL_VERIFIED_SESSION_REFRESH_FAILED =
  'Email verified, but session refresh failed';

export interface VerifyEmailOutcome {
  /** Профиль, перечитанный свежим токеном (null — перечитать не вышло). */
  user: User | null;
  /** Пара токенов выдана сервером и записана — сессия на руках. */
  sessionIssued: boolean;
}

export const verifyEmailThunk = createAsyncThunk<
  VerifyEmailOutcome,
  { email: string; code: string },
  { rejectValue: ApiErrorInfo | string; state: { auth: AuthState } }
>(
  'auth/verifyEmail',
  async ({ email, code }, { rejectWithValue, getState }) => {
    try {
      const response = await authApi.verifyEmail(email, code);
      // WEB-1217: EmailVerifiedGuard (WEB-420) reads isEmailVerified from the
      // JWT itself, so the token stored at registration stays stale (false)
      // for its full 7-day life even though the DB row just flipped — every
      // cabinet request 403s EMAIL_NOT_VERIFIED until this is persisted.
      // POST /auth/verify-email now ALSO returns a fresh pair; persist it
      // the SAME way login/register/google do (see startCleanSession above)
      // — NOT via startCleanSession itself, since this is a continuation of
      // the current session, not a new one (no project/balance wipe wanted).
      // Persisting here, inside the thunk body and before it resolves,
      // guarantees the caller's post-`.unwrap()` navigation never races the
      // write (VerifyEmail.tsx awaits this dispatch before navigating).
      const { accessToken, refreshToken } = response.data as {
        accessToken?: string;
        refreshToken?: string;
      };
      // WEB-1592: признак «сессия выдана и записана» снимается здесь, ровно по
      // факту записи, и дальше едет в результате — по нему экран решает, вести
      // человека в кабинет или на форму входа.
      const sessionIssued = Boolean(accessToken && refreshToken);
      if (accessToken && refreshToken) {
        tokenStorage.set(accessToken);
        refreshTokenStorage.set(refreshToken);
      }

      // WEB-1217 (хвост аудита 22.08): свежий токен на месте, но state.user
      // до сих пор хранит карточку, снятую сразу после регистрации, с
      // isEmailVerified: false — ProtectedRoute смотрит именно на неё и
      // после верного кода уводил человека на /login, а не в кабинет.
      // Перечитываем профиль с сервера (запрос ниже уйдёт УЖЕ со свежим
      // токеном, записанным строкой выше — guard пропустит) и кладём его в
      // state.user через fulfilled, ДО того как компонент навигирует.
      try {
        const meResponse = await authApi.me();
        if (meResponse && meResponse.data) {
          return { user: meResponse.data as User, sessionIssued };
        }
      } catch {
        // упадёт в фолбэк ниже
      }

      // WEB-1217 ревизия (аудит 22.08, находка 2 пачки 1): страховка ниже
      // легитимна ТОЛЬКО когда пара токенов реально записана этим самым
      // вызовом (accessToken && refreshToken выше). Если бэк ответил без
      // пары, me() выше уходил со СТАРЫМ токеном и получил 403
      // (EMAIL_NOT_VERIFIED не снят на бэке без свежей пары) — раньше
      // фолбэк всё равно патчил isEmailVerified:true локально и пускал в
      // кабинет со старым JWT: каждый следующий запрос там 403-ил без
      // возможности восстановиться без ручного выхода. Без свежей пары
      // корректный путь — rejectWithValue, прежний увод на /login восстановим.
      if (accessToken && refreshToken) {
        // Страховка, а не основной механизм: перечитать профиль не вышло
        // (сеть/сервер) — патчим признак локально поверх уже известного в
        // сторе пользователя, чтобы ProtectedRoute не увёл на /login ещё раз.
        const currentUser = getState().auth.user;
        if (currentUser) {
          return {
            user: { ...currentUser, isEmailVerified: true },
            sessionIssued,
          };
        }
        return { user: null, sessionIssued };
      }
      return rejectWithValue(EMAIL_VERIFIED_SESSION_REFRESH_FAILED);
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const resendVerificationCodeThunk = createAsyncThunk(
  'auth/resendVerificationCode',
  async (email: string, { rejectWithValue }) => {
    try {
      await authApi.resendVerificationCode(email);
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError(state) {
      return { ...state, error: null };
    },
    setUser(state, action: PayloadAction<User>) {
      return { ...state, user: action.payload, isAuthenticated: true };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(loginThunk.fulfilled, (state, action) => {
        // WEB-133 §F10: a 2FA challenge is NOT an authenticated session — keep
        // the user logged out until POST /auth/2fa/verify succeeds.
        if (action.payload.status === '2fa_required') {
          return { ...state, isLoading: false, error: null };
        }
        return {
          ...state,
          isLoading: false,
          user: action.payload.user,
          isAuthenticated: true,
          error: null,
        };
      })
      .addCase(loginThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(verify2faThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(verify2faThunk.fulfilled, (state, action) => ({
        ...state,
        isLoading: false,
        user: action.payload,
        isAuthenticated: true,
        error: null,
      }))
      .addCase(verify2faThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(registerThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(registerThunk.fulfilled, (state, action) => ({
        ...state,
        isLoading: false,
        user: action.payload,
        isAuthenticated: true,
        error: null,
      }))
      .addCase(registerThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(googleAuthThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(googleAuthThunk.fulfilled, (state, action) => {
        if (action.payload.status === '2fa_required') {
          return { ...state, isLoading: false, error: null };
        }
        return {
          ...state,
          isLoading: false,
          user: action.payload.user,
          isAuthenticated: true,
          error: null,
        };
      })
      .addCase(googleAuthThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(logoutThunk.fulfilled, () => ({ ...initialState, isLoading: false }))
      // WEB-1299: symmetric counterpart to the .fulfilled case above — without
      // it, the root reducer's undefined-state reset (store/index.ts, fired
      // for BOTH fulfilled and rejected) left this slice at its untouched
      // module initialState, i.e. isLoading: true forever (nothing left to
      // flip it back), which pinned the "Войти" button in a permanent
      // spinner until a full page reload.
      // WEB-1848: тот же сброс, но БЕЗ второго текста, когда выход вызван
      // истёкшей сессией — у той причины уже есть своё сообщение
      // (client.ts onSessionExpired). WEB-1299 сохраняется: isLoading всё так
      // же снимается, вход не залипает в спиннере.
      .addCase(logoutThunk.rejected, (_state, action) => ({
        ...initialState,
        isLoading: false,
        // WEB-1955: причина теперь разобранная. Здесь это НЕ отказ сервера, а
        // СВОЯ фраза кабинета («связь с сервером не удалась») — она уже на
        // языке человека, поэтому едет текстом внутри разобранной формы.
        error: action.meta.arg?.sessionExpired
          ? null
          : { status: null, message: logoutServerUnreachableMessage() },
      }))
      .addCase(fetchMeThunk.pending, (state) => ({ ...state, isLoading: true }))
      .addCase(fetchMeThunk.fulfilled, (state, action) => ({
        ...state,
        isLoading: false,
        user: action.payload,
        isAuthenticated: true,
      }))
      .addCase(fetchMeThunk.rejected, () => ({ ...initialState, isLoading: false, isAuthenticated: false }))
      .addCase(updateProfileThunk.fulfilled, (state, action) => ({
        ...state,
        user: action.payload,
        isAuthenticated: true,
        error: null,
      }))
      .addCase(updateProfileThunk.rejected, (state, action) => ({
        ...state,
        error: rejectedApiError(action),
      }))
      .addCase(requestPasswordResetThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(requestPasswordResetThunk.fulfilled, (state) => ({ ...state, isLoading: false }))
      .addCase(requestPasswordResetThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      // WEB-229 — verify-reset-password-code: CODE → TOKEN (middle step)
      .addCase(verifyResetPasswordCodeThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(verifyResetPasswordCodeThunk.fulfilled, (state) => ({ ...state, isLoading: false }))
      .addCase(verifyResetPasswordCodeThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(resetPasswordThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(resetPasswordThunk.fulfilled, (state) => ({ ...state, isLoading: false }))
      .addCase(resetPasswordThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(sendVerificationCodeThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(sendVerificationCodeThunk.fulfilled, (state) => ({ ...state, isLoading: false }))
      .addCase(sendVerificationCodeThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(verifyEmailThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(verifyEmailThunk.fulfilled, (state, action) => ({
        ...state,
        isLoading: false,
        // WEB-1217 (аудит 22.08): без этого state.user оставался устаревшим
        // (isEmailVerified: false) и ProtectedRoute уводил на /login сразу
        // после успешного подтверждения — см. комментарий в thunk выше.
        user: action.payload.user ?? state.user,
        // WEB-1592 (проявление 3): сервер выдал пару токенов вместе с
        // подтверждением, thunk её записал и перечитал профиль — человек уже
        // в сессии. Флаг здесь не поднимался вовсе (начальное значение false),
        // и кабинет отправлял на форму входа того, у кого сессия была на
        // руках. Без записанной пары флаг не трогаем: сторож ревизии WEB-1217
        // (коммит 8cc0e050) остаётся в силе.
        isAuthenticated: action.payload.sessionIssued || state.isAuthenticated,
      }))
      .addCase(verifyEmailThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(resendVerificationCodeThunk.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(resendVerificationCodeThunk.fulfilled, (state) => ({ ...state, isLoading: false }))
      .addCase(resendVerificationCodeThunk.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }));
  },
});

export const { clearError, setUser } = authSlice.actions;
export default authSlice.reducer;
